/*#
# Manifest: what virta installs

A service-compris host learns what virta needs from one declarative
manifest, installed once by someone holding the `configurator` role
(tosijs-platform#5). It declares a single collection, `virta:event`: the
append-only log every board and agent folds. There is no task collection
— current task state is a view over the events, never stored.

```
{
  manifest: 1, name: 'virta', version: '<virta version>',
  collections: {
    'virta:event': {
      schema: <the event: id, taskId, at, by, kind, payload; closed>,
      immutable: true,
      envelope: { seq: true, requireAttribution: true },
      access: [
        { role: 'author', read: 'ALL', list: 'ALL', write: 'ALL' },
        { role: 'editor', read: 'ALL', list: 'ALL', write: 'ALL' },
      ],
    },
  },
  usesRoles: ['author', 'editor'],
}
```

Names are namespaced by library (`virta:` — the separator is `:`), so two
installed libraries wanting an `event` collection cannot collide, and the
manifest may only declare names in its own namespace. Installing again with
the same version and content is a no-op; a changed schema is a new version;
a new capability declaration needs a second `POST` with `approving`.

Version history: 0.1.0 closed the schema and the beta.1 host refused every
write (its envelope stamps tripped the schema, tosijs-platform#16); 0.1.1
opened it as the interim; 0.1.2 closes it again on beta.2 and opts into the
sequence and attribution; 0.1.3 makes the log immutable (the host landed
tosijs-platform#25 in 0.2.0-beta.4): a stored event is never rewritten or
deleted, so its place in the order is permanent.

What the manifest deliberately does not say:

- **No `unique: ['id']`.** The document's path *is* its id
  (`virta:event/<id>`), so an upsert cannot collide; a unique constraint
  would only add a privileged read to every write.
- **No way to rewrite or remove an event.** `immutable: true` (0.1.3): the
  client's push is an upsert commit, and a re-sent event with an identical
  body is a no-op (the retry case); a different body under a held id is
  refused (`409 immutable`, the whole batch unwritten), and so is a delete.
  Before 0.1.3 the host would have replaced a changed body and re-sequenced
  it (tosijs-platform#25); the client's rule — an event id always carries
  the same body — is now enforced on both sides. `immutable` can be added by
  upgrade, never dropped. Removing an event (a takedown) is the host owner
  acting on the datastore directly.
- **No server-stamped `by`.** `derive: [{ op: 'principal', to: 'by' }]`
  would make provenance unforgeable, which is the design's intent, but it
  would also overwrite the historical author on an imported GitHub issue.
  The right home is the envelope's author field (tosijs-platform gap
  analysis), where the caller's `by` and the token's identity can both
  live.
- **No `seq` or `by` from the caller's side of the envelope.** The host
  assigns `_seq` at commit and stamps `_by` from the credential; a caller
  cannot send either. `envelope: { seq: true }` opts the collection into the
  total order a log needs (writes serialise, about one commit a second — an
  event log is inside that); `requireAttribution: true` refuses a write the
  host cannot attribute, so `_by` is always there.
*/

/*{ "parent": "Reference", "order": 97 }*/

import type { EventKind } from './model'
import {
  PUBLIC_COLLECTION,
  PUBLIC_FILES_AREA,
  PUBLIC_SHOWCASES,
} from './public'

export const EVENT_KINDS: EventKind[] = [
  'created',
  'retitled',
  'edited',
  'tagged',
  'untagged',
  'commented',
  'retracted',
  'attached',
  'showcased',
  'measured',
  'procedure',
  'suggested',
  'practiced',
  'resolved',
  'closed',
  'reopened',
  'subscribed',
  'unsubscribed',
  'project',
  'reattributed',
]

/** JSON Schema for one event document, as the host validates it. */
export const EVENT_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string', minLength: 1 },
    taskId: { type: 'string', minLength: 1 },
    /** RFC 3339, the writer's clock; ordering comes from `seq`, not this */
    at: { type: 'string', format: 'date-time' },
    /** the writer's identity as it knows itself; the token is the authority */
    by: { type: 'string', minLength: 1 },
    kind: { type: 'string', enum: EVENT_KINDS },
    payload: { type: 'object' },
  },
  required: ['id', 'taskId', 'at', 'by', 'kind', 'payload'],
  // closed: a log refuses fields it does not know. The host's own stamps
  // (`_created`, `_modified`, `_seq`, `_by`) are hidden from this schema
  // since 0.2.0-beta.2 (tosijs-platform#16).
  additionalProperties: false,
} as const

/**
 * The manifest's own version — bumped when the declaration changes, which
 * is not the same thing as the package version. A published version's
 * content is immutable on a host; upgrades are additive only.
 */
export const MANIFEST_VERSION = '0.1.11'

/** The roles a token needs to read and write the log. */
export const VIRTA_ROLES = ['author', 'editor'] as const

export interface VirtaManifest {
  manifest: 1
  name: 'virta'
  version: string
  collections: Record<
    string,
    {
      /** a storage area has none: its documents describe files (service-compris 0.3.0) */
      schema?: Record<string, unknown>
      /** makes this collection a storage area: files, served by /blob */
      blob?: { maxBytes: number; contentTypes?: string[] }
      unique?: string[]
      /** a stored document is never rewritten with other content, nor deleted */
      immutable?: boolean
      envelope?: { seq?: boolean; requireAttribution?: boolean }
      access: { role: string; read?: 'ALL'; list?: 'ALL'; write?: 'ALL' }[]
    }
  >
  usesRoles: string[]
}

export const EVENT_COLLECTION_NAME = 'virta:event'

/**
 * The storage area for files attached to tasks (board #2436; service-compris
 * 0.3.0): private — only the board's roles read it — and served only by
 * `/blob`. The asset types the owner named (2026-09-27): images, video,
 * audio, PDF, markdown and text. 25 MB is the endpoint's own ceiling.
 */
export const FILES_AREA_NAME = 'virta:files'
export const FILES_MAX_BYTES = 25 * 1024 * 1024
export const FILES_CONTENT_TYPES = [
  'image/*',
  'video/*',
  'audio/*',
  'application/pdf',
  'text/markdown',
  'text/plain',
]

/**
 * A public project's summary (see `PublicProject`): closed, like the log —
 * nothing but a tile's worth can be put where anyone reads.
 */
export const PUBLIC_SCHEMA = {
  type: 'object',
  properties: {
    v: { type: 'integer' },
    name: { type: 'string', minLength: 1 },
    icon: { type: 'string' },
    color: { type: 'string' },
    background: { type: 'string' },
    description: { type: 'string' },
    site: { type: 'string' },
    package: { type: 'string' },
    repo: { type: 'string' },
    version: { type: 'string' },
    retired: { type: 'boolean' },
    open: { type: 'integer' },
    inProgress: { type: 'integer' },
    blocked: { type: 'integer' },
    activity: { type: 'string' },
    showcases: {
      type: 'array',
      maxItems: PUBLIC_SHOWCASES,
      items: {
        type: 'object',
        properties: {
          title: { type: 'string' },
          text: { type: 'string' },
          link: { type: 'string' },
          alt: { type: 'string' },
          media: { type: 'string' },
          picture: { type: 'string' },
          num: { type: 'integer' },
          at: { type: 'string' },
        },
        additionalProperties: false,
      },
    },
    seq: { type: 'integer' },
  },
  required: ['v', 'name', 'open', 'seq'],
  additionalProperties: false,
} as const

/** The manifest, at its own version unless told otherwise. */
export function virtaManifest(version = MANIFEST_VERSION): VirtaManifest {
  return {
    manifest: 1,
    name: 'virta',
    version,
    collections: {
      [EVENT_COLLECTION_NAME]: {
        schema: EVENT_SCHEMA as unknown as Record<string, unknown>,
        // a log: an event, once stored, is never rewritten or deleted —
        // its place in the order is permanent (tosijs-platform#25)
        immutable: true,
        // a monotonic _seq per commit (the log's order) and an unforgeable
        // _by on every write; a write the host cannot attribute is refused
        envelope: { seq: true, requireAttribution: true },
        access: VIRTA_ROLES.map((role) => ({
          role,
          read: 'ALL' as const,
          list: 'ALL' as const,
          write: 'ALL' as const,
        })),
      },
      // files: private (no public read), the log's roles, not immutable —
      // a file nobody owns any more is collected (owner, 2026-09-28)
      [FILES_AREA_NAME]: {
        blob: {
          maxBytes: FILES_MAX_BYTES,
          contentTypes: [...FILES_CONTENT_TYPES],
        },
        access: VIRTA_ROLES.map((role) => ({
          role,
          read: 'ALL' as const,
          list: 'ALL' as const,
          write: 'ALL' as const,
        })),
      },
      // one summary per public project: anyone reads, the board's roles
      // write; derived from the log, so replaced and removed freely
      [PUBLIC_COLLECTION]: {
        schema: PUBLIC_SCHEMA as unknown as Record<string, unknown>,
        access: [
          { role: 'public', read: 'ALL' as const, list: 'ALL' as const },
          ...VIRTA_ROLES.map((role) => ({
            role,
            read: 'ALL' as const,
            list: 'ALL' as const,
            write: 'ALL' as const,
          })),
        ],
      },
      // the public projects' stored showcase pictures, copied for anyone to
      // load (owner, 2026-10-14): read by anyone, never listed — the
      // summaries name them; images and video only
      [PUBLIC_FILES_AREA]: {
        blob: {
          maxBytes: FILES_MAX_BYTES,
          contentTypes: ['image/*', 'video/*'],
        },
        access: [
          { role: 'public', read: 'ALL' as const },
          ...VIRTA_ROLES.map((role) => ({
            role,
            read: 'ALL' as const,
            list: 'ALL' as const,
            write: 'ALL' as const,
          })),
        ],
      },
    },
    usesRoles: [...VIRTA_ROLES],
  }
}
